import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Bolt as BoltIcon,
  RocketLaunch as RocketIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

import SAMPLES from "../../data/samples.json";

/**
 * Slide-in drawer with the starter-app catalog.
 *
 * Why a drawer rather than a wizard step: starters are an OPT-IN
 * shortcut, not a required first step. Users who already know what
 * they're doing can ignore it; first-timers benefit from a curated
 * pre-fill that lands them on "deploys in 3 minutes".
 *
 * Each tile shows enough that the user can pick without reading docs:
 * stack badge + tagline + "what you get" chips (port, runtime, size).
 * Clicking pre-fills the wizard via onSelect; the drawer closes.
 *
 * Dark, brand-accented aesthetic to match console.clue2.app/showcase
 * so the surfaces feel continuous.
 */
export default function GuidedDrawer({ open, onClose, onSelect }) {
  // ESC to dismiss.
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(10, 14, 39, 0.55)",
              backdropFilter: "blur(4px)",
              zIndex: 1300,
            }}
          />
          {/* Drawer panel */}
          <motion.div
            initial={{ x: "100%", opacity: 0.6 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 240 }}
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              width: "min(560px, 100vw)",
              background:
                "radial-gradient(ellipse at 80% 0%, rgba(234,88,12,0.18), transparent 40%), " +
                "linear-gradient(180deg, #0a0e27 0%, #0f172a 50%, #131a3a 100%)",
              color: "rgba(255,255,255,0.92)",
              boxShadow: "-24px 0 80px rgba(0,0,0,0.6)",
              borderLeft: "1px solid rgba(255,255,255,0.08)",
              zIndex: 1301,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <DrawerHeader onClose={onClose} />
            <DrawerBody onSelect={onSelect} onClose={onClose} />
            <DrawerFooter onClose={onClose} />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ----------------- pieces -----------------

function DrawerHeader({ onClose }) {
  return (
    <Box
      sx={{
        px: 3,
        pt: 3,
        pb: 2.5,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          background: "linear-gradient(135deg, #ea580c 0%, #f97316 60%, #fb923c 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 18px rgba(234,88,12,0.45)",
          flexShrink: 0,
        }}
      >
        <RocketIcon sx={{ color: "#fff" }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: "rgba(255,255,255,0.97)" }}>
          Quickstart with a starter
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", mt: 0.5 }}>
          Pick a stack. We pre-fill the wizard with a curated repo and proven defaults so your first deploy goes green on the first try.
        </Typography>
      </Box>
      <Tooltip title="Close (Esc)">
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)", mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function DrawerBody({ onSelect, onClose }) {
  return (
    <Box
      sx={{
        px: 3,
        py: 3,
        flex: 1,
        overflowY: "auto",
        overscrollBehavior: "contain",
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.08)", borderRadius: 3 },
      }}
    >
      <Stack spacing={2}>
        {SAMPLES.map((sample, idx) => (
          <SampleTile
            key={sample.key}
            sample={sample}
            index={idx}
            onPick={() => {
              onSelect(sample);
              onClose();
            }}
          />
        ))}
        <ComingSoonTile />
      </Stack>
    </Box>
  );
}

function SampleTile({ sample, index, onPick }) {
  const [hover, setHover] = useState(false);
  const accent = sample.color || "#fb923c";
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 + index * 0.06, ease: "easeOut" }}
    >
      <Box
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={onPick}
        sx={{
          cursor: "pointer",
          borderRadius: 3,
          overflow: "hidden",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.02) 100%)",
          border: `1px solid ${hover ? accent + "70" : "rgba(255,255,255,0.08)"}`,
          backdropFilter: "blur(20px)",
          boxShadow: hover
            ? `0 12px 36px rgba(0,0,0,0.55), 0 0 0 1px ${accent}50, 0 0 32px ${accent}25`
            : "0 4px 14px rgba(0,0,0,0.35)",
          transition: "border-color 200ms ease, box-shadow 200ms ease, transform 200ms ease",
          transform: hover ? "translateY(-2px)" : "translateY(0)",
          // Touch devices never fire onMouseEnter, so hover alone leaves
          // no visible feedback that a tap registered — the tile looks
          // dead, prompting a retry that iOS eats as a double-tap-zoom.
          "&:active": {
            borderColor: accent + "70",
            transform: "translateY(-2px)",
          },
        }}
      >
        <Box sx={{ p: 2.25, display: "flex", gap: 2, alignItems: "flex-start" }}>
          <StackIcon icon={sample.icon} accent={accent} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "1.05rem", color: "rgba(255,255,255,0.95)" }}>
                {sample.displayName}
              </Typography>
              {sample.deployReady === false && (
                <Tooltip title={sample.deployNotes || "Repo needs a small adjustment for first-time deploy."}>
                  <Chip
                    label="Beta"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      background: "rgba(251,191,36,0.18)",
                      color: "#fcd34d",
                      border: "1px solid rgba(251,191,36,0.35)",
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", lineHeight: 1.45, mb: 1.25 }}>
              {sample.tagline}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <InfoChip label={sample.shortName} accent={accent} primary />
              {sample.port && <InfoChip label={`Port ${sample.port}`} accent={accent} />}
              <InfoChip label="Auto-fills wizard" accent={accent} />
            </Stack>
          </Box>
          <motion.div
            animate={{ x: hover ? 4 : 0, opacity: hover ? 1 : 0.6 }}
            transition={{ duration: 0.2 }}
            style={{ display: "flex", alignItems: "center", gap: 4, color: accent }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>Use</Typography>
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </motion.div>
        </Box>
      </Box>
    </motion.div>
  );
}

function ComingSoonTile() {
  return (
    <Box
      sx={{
        borderRadius: 3,
        py: 2.5,
        px: 2.25,
        border: "1px dashed rgba(255,255,255,0.08)",
        textAlign: "center",
        color: "rgba(255,255,255,0.45)",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        More starters coming
      </Typography>
      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.35)" }}>
        Got a stack we should add? Tell the Clue2App team.
      </Typography>
    </Box>
  );
}

function DrawerFooter({ onClose }) {
  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderTop: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        background: "rgba(10,14,39,0.4)",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ color: "rgba(255,255,255,0.45)" }}>
        <BoltIcon sx={{ fontSize: 16, color: "#fbbf24" }} />
        <Typography variant="caption">Most starters deploy in under 3 minutes.</Typography>
      </Stack>
      <Button
        onClick={onClose}
        size="small"
        sx={{
          color: "rgba(255,255,255,0.85)",
          textTransform: "none",
          fontWeight: 500,
          "&:hover": { color: "#fed7aa" },
        }}
      >
        Start from scratch
      </Button>
    </Box>
  );
}

function InfoChip({ label, accent, primary }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 22,
        fontSize: "0.7rem",
        fontWeight: 500,
        background: primary ? `${accent}28` : "rgba(255,255,255,0.06)",
        color: primary ? accent : "rgba(255,255,255,0.7)",
        border: primary ? `1px solid ${accent}55` : "1px solid rgba(255,255,255,0.08)",
      }}
    />
  );
}

// Brand glyphs via simple-icons CDN — crisp, brand-accurate, no extra deps.
function StackIcon({ icon, accent }) {
  const color = (accent || "#fb923c").replace("#", "");
  // Map our short keys to simple-icons slugs.
  const slug = { react: "react", java: "openjdk", dotnet: "dotnet", python: "python" }[icon] || icon;
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
        border: "1px solid rgba(255,255,255,0.08)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={`https://cdn.simpleicons.org/${slug}/${color}`}
        alt=""
        sx={{ width: 26, height: 26 }}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    </Box>
  );
}
